import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Loader2, Tag, Percent, Calendar, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCoupons = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: 10,
    max_uses: 100,
    valid_until: '',
    course_id: ''  // Empty = platform-wide
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [couponsRes, coursesRes] = await Promise.all([
        axios.get(`${API}/coupons`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/courses`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCoupons(couponsRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const createCoupon = async () => {
    if (!formData.code) {
      toast.error('Please enter a coupon code');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        course_id: formData.course_id || null,
        valid_until: formData.valid_until || null
      };
      await axios.post(`${API}/coupons`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coupon created!');
      setDialogOpen(false);
      setFormData({ code: '', discount_percent: 10, max_uses: 100, valid_until: '', course_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    } finally {
      setSaving(false);
    }
  };

  const deleteCoupon = async (couponId) => {
    if (!window.confirm('Delete this coupon?')) return;

    try {
      await axios.delete(`${API}/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coupon deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const getCourseName = (courseId) => {
    if (!courseId) return 'All Courses (Platform-wide)';
    const course = courses.find(c => c.id === courseId);
    return course?.title || 'Unknown Course';
  };

  const isExpired = (validUntil) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-coupons">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin')} className="-ml-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900">Coupon Management</h1>
                <p className="text-slate-600">Create and manage discount coupons</p>
              </div>
            </div>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Coupon
            </Button>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Tag className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{coupons.length}</p>
                    <p className="text-sm text-slate-600">Total Coupons</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Percent className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {coupons.filter(c => !c.course_id).length}
                    </p>
                    <p className="text-sm text-slate-600">Platform-wide</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {coupons.reduce((sum, c) => sum + (c.uses || 0), 0)}
                    </p>
                    <p className="text-sm text-slate-600">Total Uses</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Coupons List */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">All Coupons</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : coupons.length > 0 ? (
                <div className="divide-y divide-slate-200">
                  {coupons.map((coupon) => (
                    <div key={coupon.id} className="py-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Tag className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <code className="font-mono font-bold text-lg text-slate-900">{coupon.code}</code>
                            <Badge className="bg-green-100 text-green-800">{coupon.discount_percent}% OFF</Badge>
                            {isExpired(coupon.valid_until) && (
                              <Badge variant="destructive">Expired</Badge>
                            )}
                            {!coupon.course_id && (
                              <Badge className="bg-purple-100 text-purple-800">Platform-wide</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600">
                            {getCourseName(coupon.course_id)} • {coupon.uses || 0}/{coupon.max_uses} uses
                          </p>
                          {coupon.valid_until && (
                            <p className="text-xs text-slate-400">
                              Expires: {new Date(coupon.valid_until).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => deleteCoupon(coupon.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No coupons created yet</p>
                  <Button className="mt-4" onClick={() => setDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Coupon
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Coupon Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Coupon</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Coupon Code *</Label>
              <Input
                placeholder="e.g., SAVE20, SUMMER2025"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className="font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount %</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData({ ...formData, discount_percent: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Max Uses</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: parseInt(e.target.value) || 100 })}
                />
              </div>
            </div>
            <div>
              <Label>Valid Until (Optional)</Label>
              <Input
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
              />
            </div>
            <div>
              <Label>Apply To</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={formData.course_id}
                onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
              >
                <option value="">All Courses (Platform-wide)</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>{course.title}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Platform-wide coupons work on any course
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={createCoupon} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Coupon
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCoupons;
